import React, { useState, useEffect, useRef } from "react";
import _ from "lodash";
import { toast } from "react-toastify";
import Layout from "../../components/Layout";
import SummaryList from "./SummaryList";
import { getSummaries } from "../../services";
import Pagination from "../../components/Pagination";
import moment from "moment";
import { useSelector } from "react-redux";
import { CSVLink } from "react-csv";
import { motion } from "framer-motion";
import { Users, Calendar as CalendarIcon, Search, Download, LayoutList, Filter, ChevronDown } from "lucide-react";
import DateBox from "../../components/DateBox";
import MemberSelect from "../../components/MemberSelect";

export default function Summary() {
  const [startDate, setStartDate] = useState(moment().startOf("month").toDate());
  const [endDate, setEndDate] = useState(moment().toDate());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [allMessage, setAllMessage] = useState({ SummaryList: [], TotalCount: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const [errMsg, setErrMsg] = useState({ err: false, msg: "", color: "warning" });
  const [selectedMember, setSelectedMember] = useState('');
  const members = useSelector((state) => state.members.members);
  const [offset, setOffset] = useState(0);
  const itemsPerPage = 10;
  const csvLinkRef = useRef();
  const [cSVData, setcSVData] = useState([]);

  const notify = () => {
    toast.error("Please fill in all fields!", {
      position: "top-right",
      className: "dark:bg-gray-900 dark:text-white",
    });
  };

  const setDateRange = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const fetchMessage = async (offset) => {
    if (_.isNil(startDate) || _.isNil(endDate)) {
      setErrMsg({ err: true, msg: "Please fill in all fields", color: "red" });
      notify();
      return;
    }
    setIsLoading(true);
    try {
      const formattedStartDate = moment(startDate).format("YYYY-MM-DD");
      const formattedEndDate = moment(endDate).format("YYYY-MM-DD");

      const response = await getSummaries(
        selectedMember,
        formattedStartDate,
        formattedEndDate,
        offset,
        itemsPerPage
      );
      const summaryList = response?.data?.Result;
      setAllMessage({
        SummaryList: Array.isArray(summaryList?.SummaryList) ? summaryList.SummaryList : [],
        TotalCount: summaryList?.TotalCount || 0,
      });
    } catch (err) {
      setAllMessage({ SummaryList: [], TotalCount: 0 });
      console.error("Error fetching summary:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMessage(offset);
  }, [offset]);

  const fetchsummaryCSV = async () => {
    if (_.isNil(startDate) || _.isNil(endDate)) {
      setErrMsg({ err: true, msg: "Please fill in all fields", color: "red" });
      notify();
      return;
    }
    setIsLoading(true);
    try {
      const formattedStartDate = moment(startDate).format("YYYY-MM-DD");
      const formattedEndDate = moment(endDate).format("YYYY-MM-DD");

      const response = await getSummaries(
        selectedMember,
        formattedStartDate,
        formattedEndDate,
        0,
        allMessage.TotalCount
      );
      const summaryList = response?.data?.Result;
      setcSVData(summaryList?.SummaryList || []);
    } catch (err) {
      setcSVData([]);
      console.error("Error fetching summary:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const prepareCSVData = () => {
    return cSVData.map((message) => ({
      MemberName: message.MemberName || "N/A",
      Date: message.SummaryDate ? moment(message.SummaryDate).format("YYYY-MM-DD") : "N/A",
      Summary: message.Summary || "N/A",
    }));
  };

  const headers = [
    { label: "Member Name", key: "MemberName" },
    { label: "Date", key: "Date" },
    { label: "Summary", key: "Summary" },
  ];

  const handleCsvDownload = async () => {
    setIsLoadingSummary(true);
    await fetchsummaryCSV();
    setTimeout(() => {
      csvLinkRef.current.link.click();
    }, 1000);
    setIsLoadingSummary(false);
  };

  return (
    <Layout>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="px-4 py-8 max-w-7xl mx-auto"
      >
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-500/10 rounded-xl">
              <LayoutList className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Summary Log</h2>
              <p className="text-gray-500 dark:text-gray-400 text-sm">Review activity summaries across the team</p>
            </div>
          </div>

          {allMessage.SummaryList.length > 0 && (
            <button
              type="button"
              className={`flex items-center gap-2 px-5 py-2.5 bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 font-bold rounded-xl border border-blue-100 dark:border-blue-500/20 hover:bg-blue-100 dark:hover:bg-blue-500/20 transition-all active:scale-95 shadow-sm hover:shadow-blue-500/10 ${isLoadingSummary ? "opacity-50 cursor-not-allowed" : ""
                }`}
              onClick={handleCsvDownload}
              disabled={isLoadingSummary}
            >
              <Download className={`w-5 h-5 ${isLoadingSummary ? "animate-bounce" : ""}`} />
              {isLoadingSummary ? "Generating..." : "Export CSV"}
            </button>
          )}
        </div>

        <div className="bg-white dark:bg-gray-900/50 backdrop-blur-xl border border-gray-100 dark:border-blue-gray-900 p-6 rounded-2xl shadow-xl shadow-gray-200/50 dark:shadow-none mb-10">
          <div className="flex flex-col lg:flex-row items-end gap-6">
            <div className="w-full lg:w-auto flex flex-col md:flex-row items-end gap-x-4 gap-y-6">
              {/* Member Selection */}
              <div className="space-y-2 md:w-64 lg:w-72">
                <label className="block text-sm text-gray-700 dark:text-gray-300 ms-1">
                  Team Member
                </label>
                <MemberSelect
                  members={members}
                  selectedMember={selectedMember}
                  onSelect={setSelectedMember}
                />
              </div>

              {/* Date Range Picker */}
              <div className="space-y-2 md:w-[22rem] lg:w-[24rem]">
                <label className="block text-sm  text-gray-700 dark:text-gray-300 ms-1">
                  Date Range
                  
                </label>
                <div className="relative group">
                  <button
                    type="button"
                    onClick={() => setShowDatePicker(true)}
                    className="w-full flex items-center justify-between p-1.5 bg-gray-50 dark:bg-black/50 border border-gray-100 dark:border-blue-gray-900 rounded-xl text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500/20 outline-none transition-all cursor-pointer hover:bg-gray-100 dark:hover:bg-white/10 group shadow-sm dark:shadow-none"
                  >
                    <div className="flex items-center gap-2 flex-1">
                      <div className="bg-white dark:bg-white/10 px-4 py-2 rounded-lg font-medium tracking-wide shadow-sm dark:shadow-none border border-gray-100 dark:border-transparent">
                        {startDate ? moment(startDate).format("DD MMM YYYY") : "Start Date"}
                      </div>
                      <span className="text-gray-400 dark:text-gray-500 font-medium text-[13px] px-1">To</span>
                      <div className="bg-white dark:bg-white/10 px-4 py-2 rounded-lg font-medium tracking-wide shadow-sm dark:shadow-none border border-gray-100 dark:border-transparent">
                        {endDate ? moment(endDate).format("DD MMM YYYY") : "End Date"}
                      </div>
                    </div>
                    <div className="px-3 border-l border-gray-100 dark:border-white/10 ml-2">
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-300 ${showDatePicker ? 'rotate-180 text-blue-500' : ''}`} />
                    </div>
                  </button>

                  {showDatePicker && (
                    <DateBox
                      mode="range"
                      initialStartDate={startDate}
                      initialEndDate={endDate}
                      onApply={(dates) => {
                        setDateRange(dates);
                        setShowDatePicker(false);
                      }}
                      onClose={() => setShowDatePicker(false)}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Search Button */}
            <div className="w-full lg:w-auto">
              <button
                type="button"
                className={`w-full lg:w-48 flex items-center justify-center gap-2 py-3 px-8 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl shadow-lg shadow-blue-500/20 transition-all active:scale-95 ${isLoading ? "opacity-75 cursor-wait" : ""
                  }`}
                onClick={() => fetchMessage(offset)}
                disabled={isLoading}
              >
                {isLoading ? (
                  <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : (
                  <>
                    <Search className="w-5 h-5" />
                    <span>Search</span>
                  </>
                )}
              </button>
            </div>
          </div>
          {errMsg.err && (
            <motion.p
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              className="text-red-500 text-xs mt-4 font-medium flex items-center gap-1"
            >
              <span className="w-1 h-1 bg-red-500 rounded-full" />
              {errMsg.msg}
            </motion.p>
          )}
        </div>

        <SummaryList message={allMessage} />

        <div className="mt-8 flex justify-center">
          {!_.isEmpty(allMessage.SummaryList) && allMessage.TotalCount > 0 ? (
            <Pagination
              totalCount={allMessage.TotalCount}
              itemsPerPage={itemsPerPage}
              onPageChange={(newOffset) => {
                setOffset(newOffset);
                fetchMessage(newOffset);
              }}
            />
          ) : null}
        </div>

        <CSVLink
          data={prepareCSVData()}
          headers={headers}
          filename={`team-summary-${moment().format("YYYY-MM-DD")}.csv`}
          ref={csvLinkRef}
          className="hidden"
        />
      </motion.div>
    </Layout>
  );
}
